import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { sortBy } from "lodash";
import { useUpdateEpisodeMutation } from "~/graphql/__generated__/graphql";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import { useSnackbar } from "~/hooks/useSnackbar";
import PrecautionOctagon from "./PrecautionOctagon";

type Props = {
  episodeId: number;
  episodeLabel: string;
  currentPrecautionIds: number[];
  onClose: () => void;
  onSaved?: () => void;
};

export default function EpisodePrecautionsDialog({ episodeId, episodeLabel, currentPrecautionIds, onClose, onSaved }: Props) {
  const { precautions } = useOrgConfig();
  const { setErrorMessage } = useSnackbar();
  const [selectedIds, setSelectedIds] = useState<number[]>(currentPrecautionIds);
  const [updateEpisode, { loading }] = useUpdateEpisodeMutation({
    awaitRefetchQueries: true,
  });

  const all = Object.values(precautions).filter((p): p is NonNullable<typeof p> => p != null);
  const currentSet = new Set(currentPrecautionIds);
  const visible = all.filter((p) => p.active || currentSet.has(p.id));
  const options = sortBy(visible, "name");

  const toggle = (precautionId: number) => {
    setSelectedIds((prev) => prev.includes(precautionId)
      ? prev.filter((id) => id !== precautionId)
      : [...prev, precautionId]);
  };

  const handleSave = async () => {
    try {
      await updateEpisode({
        variables: {
          episodeId,
          organizationalUnitId: null,
          active: null,
          autoschedulerGroup: null,
          careRequirements: null,
          precautionIds: selectedIds,
        },
        awaitRefetchQueries: true,
        refetchQueries: "active",
      });
      onSaved?.();
      onClose();
    } catch (error) {
      console.error(error);
      setErrorMessage("Couldn't save precautions. Please try again.");
    }
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Precautions for {episodeLabel} Episode
      </DialogTitle>
      <DialogContent dividers>
        {options.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No precautions are configured for this organization yet. Add some under Operate &gt; Precautions.
          </Typography>
        ) : (
          options.map((precaution) => (
            <FormControlLabel
              key={precaution.id}
              sx={{ display: "flex", width: "100%", mx: 0 }}
              control={
                <Checkbox
                  checked={selectedIds.includes(precaution.id)}
                  onChange={() => toggle(precaution.id)}
                  disabled={loading}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PrecautionOctagon color={precaution.color} size={16} />
                  <span>{precaution.name}</span>
                  {!precaution.active && (
                    <Typography variant="caption" color="text.secondary">(inactive)</Typography>
                  )}
                </Box>
              }
            />
          ))
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          loading={loading}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
